
import React, { useState, useEffect } from 'react';
import { User, ClinicInvite } from '../types';
import { Button, Input, Card } from './ui';
import { api } from '../services/api';
import { UserPlus, Trash2, Shield, Eye, EyeOff, Check, X, Mail } from 'lucide-react';

export const ClinicManagement = () => {
  const [members, setMembers] = useState<User[]>([]);
  const [invites, setInvites] = useState<ClinicInvite[]>([]);
  const [isInviting, setIsInviting] = useState(false);
  
  // Invite Form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [canSeeFinancials, setCanSeeFinancials] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    refreshList();
  }, []);

  const refreshList = () => {
    api.getClinicMembers().then(setMembers);
    api.getClinicInvites().then(setInvites);
  };

  const handleInvite = async () => {
    if (!name || !email) return alert("Name and Email required");
    setLoading(true);
    try {
      await api.inviteClinicMember(name, email, canSeeFinancials);
      setIsInviting(false);
      setName(''); setEmail(''); setCanSeeFinancials(false);
      refreshList();
    } catch (e: any) {
      alert("Failed to invite: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePermission = async (member: User, canSee: boolean) => {
    try {
      await api.updateProfile(member.id, { canSeeFinancials: canSee });
      setMembers(members.map(m => m.id === member.id ? { ...m, canSeeFinancials: canSee } : m));
    } catch (e) {
      console.error(e);
    }
  };

  const handleRemoveMember = async (id: string) => {
    if (confirm("Remove this moderator? They will no longer have access to your clinic's data.")) {
      await api.deleteClinicMember(id);
      refreshList();
    }
  };

  const handleCancelInvite = async (id: string) => {
    await api.deleteInvite(id);
    refreshList();
  };

  return (
    <div className="space-y-8 animate-fade-in pb-20">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">My Clinic Team</h1>
          <p className="text-zinc-400">Manage access for your staff and assistants.</p>
        </div>
        <Button onClick={() => setIsInviting(true)}><UserPlus size={18}/> Add Moderator</Button>
      </div>

      {isInviting && (
        <Card className="bg-zinc-900 border-zinc-800 animate-slide-up">
          <h3 className="font-bold text-white mb-4">Invite New Moderator</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input label="Name" value={name} onChange={(e: any) => setName(e.target.value)} placeholder="e.g. Assistant Sarah" />
            <Input label="Email" value={email} onChange={(e: any) => setEmail(e.target.value)} placeholder="staff@clinic.com" />
          </div>
          
          <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all mb-6 ${canSeeFinancials ? 'bg-green-900/20 border-green-500/50' : 'bg-zinc-950 border-zinc-800'}`}>
             <div className={`w-5 h-5 rounded flex items-center justify-center border ${canSeeFinancials ? 'bg-green-500 border-green-500' : 'border-zinc-600'}`}>
                {canSeeFinancials && <Check size={14} className="text-white"/>}
             </div>
             <input type="checkbox" className="hidden" checked={canSeeFinancials} onChange={(e) => setCanSeeFinancials(e.target.checked)} />
             <div>
                <span className="text-white font-bold text-sm block">Allow Financial Access</span>
                <span className="text-zinc-500 text-xs">Can see prices, invoices, and wallet balance.</span>
             </div>
          </label>

          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setIsInviting(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={loading}>{loading ? 'Inviting...' : 'Send Invitation'}</Button>
          </div>
        </Card>
      )}

      {/* Active Members */}
      <div className="space-y-4">
        <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-wider">Active Staff</h3>
        <div className="grid gap-4">
          {members.map(m => (
            <div key={m.id} className="flex flex-col md:flex-row justify-between items-center p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
              <div className="flex items-center gap-4 mb-4 md:mb-0 w-full md:w-auto">
                <div className="w-10 h-10 rounded-full bg-blue-900/30 flex items-center justify-center text-blue-400 font-bold border border-blue-500/30">
                  {m.name.charAt(0)}
                </div>
                <div>
                  <h4 className="font-bold text-white">{m.name}</h4>
                  <p className="text-xs text-zinc-500">{m.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-6 w-full md:w-auto justify-between">
                <div className="flex items-center gap-2">
                   <button 
                     onClick={() => handleUpdatePermission(m, !m.canSeeFinancials)}
                     className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold transition-all ${m.canSeeFinancials ? 'bg-green-900/20 text-green-400 border-green-500/30 hover:bg-green-900/30' : 'bg-red-900/10 text-red-400 border-red-900/30 hover:bg-red-900/20'}`}
                   >
                      {m.canSeeFinancials ? <Eye size={14}/> : <EyeOff size={14}/>}
                      {m.canSeeFinancials ? 'Financials Visible' : 'Financials Hidden'}
                   </button>
                </div>
                <button onClick={() => handleRemoveMember(m.id)} className="text-zinc-500 hover:text-red-400 p-2"><Trash2 size={18}/></button>
              </div>
            </div>
          ))}
          {members.length === 0 && <p className="text-zinc-500 italic text-sm">No active moderators.</p>}
        </div>
      </div>

      {/* Pending Invites */}
      {invites.length > 0 && (
        <div className="space-y-4 pt-6 border-t border-zinc-800">
          <h3 className="text-sm font-bold text-zinc-500 uppercase tracking-wider">Pending Invitations</h3>
          <div className="grid gap-4">
            {invites.filter(i => i.status === 'PENDING').map(invite => (
              <div key={invite.id} className="flex justify-between items-center p-4 bg-zinc-900/50 border border-zinc-800 border-dashed rounded-xl">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-500">
                    <Mail size={18}/>
                  </div>
                  <div>
                    <h4 className="font-bold text-zinc-300">{invite.name}</h4>
                    <p className="text-xs text-zinc-500">{invite.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                   <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded">
                      {invite.canSeeFinancials ? 'Financial Access' : 'No Financials'}
                   </span>
                   <button onClick={() => handleCancelInvite(invite.id)} className="text-zinc-500 hover:text-red-400 text-xs">Revoke</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
